/**
 * API client for the Sandbox ManagementService.
 *
 * Calls go through: /sandbox-api/* -> Vite proxy -> ManagementService
 * Auth tokens are acquired via MSAL for the sandbox mgmt scope.
 */

import { getSandboxMgmtToken } from './auth';
import { config } from '@/config';

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public code?: string,
  ) {
    super(`API ${status}: ${detail}`);
    this.name = 'ApiError';
  }
}

/**
 * Request function for the Sandbox ManagementService.
 * Routes through the /sandbox-api Vite proxy to avoid CORS.
 * Acquires a bearer token via MSAL for the sandbox mgmt scope.
 */
async function sandboxMgmtRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getSandboxMgmtToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'api-version': '1.0',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const resp = await fetch(`${config.sandboxApiPrefix}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (resp.status === 204) {
    return undefined as T;
  }

  if (!resp.ok) {
    let detail = resp.statusText;
    let code: string | undefined;
    try {
      const err = await resp.json();
      // ManagementService format: { error: { code, message, details } }
      if (err.error) {
        detail = err.error.message || detail;
        code = err.error.code;
      } else {
        detail = err.detail || err.message || detail;
      }
    } catch {
      // ignore parse errors
    }
    throw new ApiError(resp.status, detail, code);
  }

  return resp.json();
}

// --- Sandboxes (Sandbox ManagementService — real API) ---

export function fetchPoolSandboxes(poolId: string) {
  return sandboxMgmtRequest<{ value: unknown[] }>('GET', `/pools/${poolId}/sandboxes`);
}

export function checkoutSandbox(poolId: string, body: unknown) {
  return sandboxMgmtRequest<unknown>('POST', `/pools/${poolId}/sandboxes`, body);
}

export function fetchSandbox(poolId: string, sandboxId: string) {
  return sandboxMgmtRequest<unknown>('GET', `/pools/${poolId}/sandboxes/${sandboxId}`);
}

export function deleteSandbox(poolId: string, sandboxId: string) {
  return sandboxMgmtRequest<void>('DELETE', `/pools/${poolId}/sandboxes/${sandboxId}`);
}
